/**
 * Program 12.1 UX3 — Prequalification requirement matrix (meta_json.requirements).
 */
export type PrequalRequirementStatus =
  | 'COMPLETE'
  | 'IN_REVIEW'
  | 'MISSING'
  | 'NOT_APPLICABLE';

export type PrequalRequirementRow = {
  id: string;
  section: string;
  requirement: string;
  status: PrequalRequirementStatus;
  evidence?: string;
  owner?: string;
  due_date?: string;
  issue?: string;
  critical?: boolean;
};

export type PrequalRequirementMeta = {
  requirements?: PrequalRequirementRow[];
};

export function parsePrequalRequirements(meta: unknown): PrequalRequirementRow[] {
  if (!meta || typeof meta !== 'object') return [];
  const m = meta as PrequalRequirementMeta;
  return Array.isArray(m.requirements) ? m.requirements : [];
}

export type PrequalCompleteness = {
  total: number;
  complete: number;
  inReview: number;
  missing: number;
  notApplicable: number;
  completionPct: number;
  criticalMissing: number;
};

export function computePrequalCompleteness(rows: PrequalRequirementRow[]): PrequalCompleteness {
  const applicable = rows.filter((r) => r.status !== 'NOT_APPLICABLE');
  const total = applicable.length;
  const complete = applicable.filter((r) => r.status === 'COMPLETE').length;
  const inReview = applicable.filter((r) => r.status === 'IN_REVIEW').length;
  const missing = applicable.filter((r) => r.status === 'MISSING').length;
  const notApplicable = rows.length - applicable.length;
  const criticalMissing = rows.filter((r) => r.critical && r.status === 'MISSING').length;
  const completionPct = total > 0 ? Math.round((complete / total) * 100) : 0;
  return { total, complete, inReview, missing, notApplicable, completionPct, criticalMissing };
}

export type PrequalSubmissionReadiness = {
  ready: boolean;
  blockers: string[];
};

export function computePrequalSubmissionReadiness(
  rows: PrequalRequirementRow[],
  baseBlocked: boolean,
  baseReason?: string
): PrequalSubmissionReadiness {
  const blockers: string[] = [];
  if (baseBlocked && baseReason) blockers.push(baseReason);
  for (const r of rows) {
    if (r.critical && r.status === 'MISSING') {
      blockers.push(`${r.requirement}${r.issue ? `: ${r.issue}` : ''}`);
    }
  }
  return { ready: blockers.length === 0, blockers };
}

export function mergeRequirementsMeta(
  meta: Record<string, unknown> | null | undefined,
  requirements: PrequalRequirementRow[]
): Record<string, unknown> {
  return { ...(meta || {}), requirements };
}
